import {
    Component,
    ComponentFactoryResolver,
    ComponentRef,
    ElementRef,
    HostListener,
    OnDestroy,
    OnInit,
    Type,
    ViewChild,
    ViewContainerRef,
} from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { DrawerConfig } from './drawer-config';
import { DrawerRef } from './drawer-ref';

@Component({
    selector: 'app-drawer',
    templateUrl: './drawer.component.html',
    styleUrls: ['./drawer.component.scss'],
    animations: [
        trigger('slideIn', [
            state('void', style({ transform: 'translateX(100%)' })),
            state('enter', style({ transform: 'translateX(0)' })),
            state('leave', style({ transform: 'translateX(100%)' })),
            transition('void => enter', animate('300ms cubic-bezier(0.25, 0.8, 0.25, 1)')),
            transition('enter => leave', animate('200ms cubic-bezier(0.25, 0.8, 0.25, 1)')),
        ]),
        trigger('fadeIn', [
            state('void', style({ opacity: 0 })),
            state('enter', style({ opacity: 1 })),
            state('leave', style({ opacity: 0 })),
            transition('void => enter', animate('300ms ease-in')),
            transition('enter => leave', animate('200ms ease-out')),
        ]),
    ],
})
export class DrawerComponent implements OnInit, OnDestroy {
    @ViewChild('dynamicComponentContainer', { read: ViewContainerRef, static: true })
    dynamicComponentContainer!: ViewContainerRef;

    config!: DrawerConfig;
    drawerRef!: DrawerRef;
    animationState: 'void' | 'enter' | 'leave' = 'void';
    private componentRef?: ComponentRef<any>;

    constructor(
        private componentFactoryResolver: ComponentFactoryResolver,
        private elementRef: ElementRef
    ) { }

    ngOnInit(): void {
        // Start animation
        setTimeout(() => {
            this.animationState = 'enter';
        }, 10);

        // Load dynamic component if provided
        if (this.config.component) {
            this.loadComponent(this.config.component);
        }
    }

    ngOnDestroy(): void {
        if (this.componentRef) {
            this.componentRef.destroy();
        }
    }

    @HostListener('document:keydown.escape', ['$event'])
    onEscapeKey(event: KeyboardEvent): void {
        if (!this.config.disableClose) {
            this.close();
        }
    }

    private loadComponent(component: Type<any>): void {
        const componentFactory = this.componentFactoryResolver.resolveComponentFactory(component);
        this.dynamicComponentContainer.clear();

        this.componentRef = this.dynamicComponentContainer.createComponent(componentFactory);

        // Inject data and drawerRef into the component
        const instance = this.componentRef.instance;
        if (this.config.data && instance) {
            Object.assign(instance, { data: this.config.data });
        }
        if (instance) {
            Object.assign(instance, { drawerRef: this.drawerRef });
        }
    }

    onBackdropClick(): void {
        if (!this.config.disableClose && this.config.hasBackdrop) {
            this.close();
        }
    }

    close(result?: any): void {
        this.animationState = 'leave';
        // Wait for animation to complete before closing
        setTimeout(() => {
            this.drawerRef.close(result);
        }, 200);
    }

    get drawerWidth(): string {
        const w = this.config.width || '600px';
        // Nếu đã là % hoặc vw hoặc đã có min()/max() thì giữ nguyên
        // Nếu là px cố định thì giới hạn bằng 95vw để không tràn màn hình iPad/tablet
        if (w.includes('%') || w.includes('vw') || w.includes('min(') || w.includes('max(') || w.includes('clamp(')) {
            return w;
        }
        return `min(${w}, 95vw)`;
    }

    get drawerPosition(): string {
        return this.config.position || 'right';
    }

    get showBackdrop(): boolean {
        return this.config.hasBackdrop !== false;
    }
}
